import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApplicationStatus, ApplicationUploadFilesInput, CreateOrUpdateSlapContact, CreateSlapInput, CreateSlapOutput, ExternalSystemEnum, GeneralFileDto, GeneralFileInfoDto, GetSlapDocumentsInput, GetSlapFileInput, GetSlapInvoiceAddressOutput, GetSlapOutput, InvoiceAddressDetailDto, LiaTypeServiceProxy, PersonApplicationSummaryDto, SlapBusinessDto, SlapBusinessServiceProxy, SlapContactDto, SlapContactServiceProxy, SlapDocumentServiceProxy, SlapDocumentsOutput, SlapDto, SlapInvoiceAddressDto, SlapInvoiceAddressServiceProxy, SlapPersonDto, SlapPersonServiceProxy, SlapProductPreparationDto, SlapProductPreparationServiceProxy, SlapRangeOfProductsDto, SlapRangeOfProductsServiceProxy, SlapServiceProxy, SlapStatus } from './../../../shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';
import { SelectItem } from '@node_modules/primeng/api';
import { AppConsts } from '@shared/AppConsts';
@Injectable({providedIn: 'root'})
export class SlapService extends AppComponentBase {

    slap: SlapDto;
    slapResultDocuments: GeneralFileInfoDto[];

    private slapsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly slapsForPerson$ = this.slapsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private liaService: LiaTypeServiceProxy,
        private slapService: SlapServiceProxy,
        private personService: SlapPersonServiceProxy,
        private contactService: SlapContactServiceProxy,
        private businessService: SlapBusinessServiceProxy,
        private rangeOfProductsService: SlapRangeOfProductsServiceProxy,
        private productPreparationService: SlapProductPreparationServiceProxy,
        private invoiceAddressService: SlapInvoiceAddressServiceProxy,
        private documentService: SlapDocumentServiceProxy,
    ) {
        super(injector);
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaService.getExternalRegisterByProfessionId(professionId);
    }

    createSlap(input: CreateSlapInput): Observable<CreateSlapOutput> {
        return this.slapService.createSlap(input).pipe(tap((output: CreateSlapOutput) => {
            this.slap = null;
        }));
    }

    deleteSlap(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getSlap(caseId: string): Observable<GetSlapOutput> {
        return this.slapService.getSlap(caseId).pipe(
            tap((output: GetSlapOutput) => {
                this.slap = output.slap;
            })
        );
    }

    closeSlap(): void {
        this.slap = null;
    }

    getMySlaps(): Observable<PersonApplicationSummaryDto[]> {
        return this.slapService.getMySlaps();
    }


    loadSlapsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln) {
            return this.slapService.getSlapsForPerson(gln).pipe(
                tap(slaps => this.slapsForPersonSubject.next(slaps))
            );
        } else {
            this.slapsForPersonSubject.next([]);
            return of([]);
        }
    }

    isInSlapStatus(expectedStatus: SlapStatus): boolean {
        return this.slap.statusId === expectedStatus;
    }

    getProfessionalLicencePersonIdForSlap(): Observable<number> {
        return this.slapService.getProfessionalLicencePersonIdForSlap(this.caseId);
    }

    releaseSlap(comment: string): Observable<void> {
        return this.slapService.releaseSlap(this.slap.application.caseId, comment);
    }

    getNextStatus(status: SlapStatus): Observable<SlapStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.slapService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === SlapStatus.Release) {
                return SlapStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: SlapStatus): string {
        return this.applicationService.getNextStep(SlapService.getUrlForStep(nextStep));
    }

    getFile(step: SlapStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetSlapFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.documentService.getFile(input);
    }

    getDocuments(step: SlapStatus): Observable<SlapDocumentsOutput> {
        const input = new GetSlapDocumentsInput();
        input.caseId = this.caseId;
        input.step = step;
        return this.documentService.getDocuments(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.documentService.uploadFiles(input);
    }

    deleteFile(step: SlapStatus, id: number): Observable<void> {
        return this.documentService.deleteFile(this.caseId, step, id);
    }

    get caseId(): string {
        return this.applicationService.caseId;
    }

    get status(): ApplicationStatus {
        return this.applicationService.status;
    }

    get professionId(): string {
        return this.slap?.professionId;
    }

    getApplicantName(): Observable<string> {
        return of(this.slap.application.name);
    }

    setSlapPersonStepToReview() {
        this.applicationService.setStepToReview(SlapStatus.Person);
    }

    getPerson(): Observable<SlapPersonDto> {
        return this.personService.get(this.caseId);
    }

    performRegisterCheck(caseId = this.caseId): Observable<SlapPersonDto> {
        this.applicationService.setStepToReview(SlapStatus.Person);
        return this.personService.performRegisterCheck(caseId);
    }

    acceptRegisterCheck(): Observable<SlapStatus> {
        this.applicationService.setStepToReview(SlapStatus.Person);
        return this.personService.acceptRegisterCheck(this.caseId).pipe(
            tap((newStatusId: SlapStatus) => {
                this.slap.statusId = newStatusId;
            })
        );
    }

    getContact(): Observable<SlapContactDto> {
        return this.contactService.get(this.caseId);
    }

    createOrUpdateContact(input: CreateOrUpdateSlapContact): Observable<any> {
        this.applicationService.setStepToReview(SlapStatus.Contact);
        return this.contactService.createOrUpdate(input).pipe(
            tap((newStatusId: SlapStatus) => {
            this.slap.statusId = newStatusId;
            },
            map((status: SlapStatus) => null)
        ));
    }

    getBusinesses(): Observable<SlapBusinessDto[]> {
        return this.businessService.getList(this.caseId);
    }

    createOrUpdateBusiness(caseId: string, input: SlapBusinessDto[]): Observable<void> {
        this.applicationService.setStepToReview(SlapStatus.Business);
        return this.businessService.createOrUpdate(caseId, input).pipe(
            tap((newStatusId: SlapStatus) => {
                this.slap.statusId = newStatusId;
            }),
            map((status: SlapStatus) => null)
        );
    }

    getRangeOfProducts(): Observable<SlapRangeOfProductsDto> {
        return this.rangeOfProductsService.get(this.caseId);
    }

    updateRangeOfProducts(rangeOfProducts: SlapRangeOfProductsDto): Observable<any> {
        this.applicationService.setStepToReview(SlapStatus.RangeOfProducts);
        return this.rangeOfProductsService.createOrUpdate(this.caseId, rangeOfProducts).pipe(tap((newStatusId: SlapStatus) => {
            this.slap.statusId = newStatusId;
        }));
    }

    getProductPreparation(): Observable<SlapProductPreparationDto> {
        return this.productPreparationService.get(this.caseId);
    }

    createOrUpdateProductPreparation(productPreparation: SlapProductPreparationDto): Observable<any> {
        this.applicationService.setStepToReview(SlapStatus.ProductPreparation);
        return this.productPreparationService.createOrUpdate(this.caseId, productPreparation).pipe(tap((newStatusId: SlapStatus) => {
            this.slap.statusId = newStatusId;
        }));
    }

    //createOrUpdateLiaPersonWithRegisterCheck(input: CreateOrUpdateLiaPersonWithRegisterCheckInput): Observable<CreateOrUpdateLiaPersonOutputDto> {
    //    this.setStepToReview(LiaStatus.Person);
    //    return this.liaPersonService.createOrUpdateLiaPersonWithRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonOutputDto) => {
    //        this.lia.liaPerson = output.liaPerson;
    //    }));
    //}

    //acceptRegisterCheck(): Observable<LiaStatus> {
    //    this.setStepToReview(LiaStatus.Person);
    //    return this.liaPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: LiaStatus) => {
    //        this.lia.currentStep = status;
    //    }));
    //}

    getInvoiceAddress(): Observable<GetSlapInvoiceAddressOutput> {
        return this.invoiceAddressService.get(this.caseId);
    }

    createOrUpdateInvoiceAddress(invoiceAddress: SlapInvoiceAddressDto): Observable<any> {
        this.applicationService.setStepToReview(SlapStatus.InvoiceAddress);
        return this.invoiceAddressService.createOrUpdate(this.caseId, invoiceAddress).pipe(tap((newStatusId: SlapStatus) => {
            this.slap.statusId = newStatusId;
        }));
    }

    createAddressDropdownList(contact: InvoiceAddressDetailDto, employments: SlapBusinessDto[], onlyPrivateAddressAllowed: boolean): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(contact) });

        if (!onlyPrivateAddressAllowed) {
            employments?.forEach((employment) => {
                // Use employment id as item value
                addresses.push({ value: employment.id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(employment) });
            });

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: InvoiceAddressDetailDto) {
        let str = contact?.street;

        if (contact?.streetNr && contact.streetNr.length > 0) {
            str = str.concat(' ');
            str = str.concat(contact.streetNr);
        }

        str = str?.concat(', ');
        str = str?.concat(contact?.city);

        return str;
    }

    private getEmploymentAddressDescription(employment: SlapBusinessDto) {
        let str = employment?.name;

        if (employment.street?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.street);

            if (employment.streetNr?.length > 0) {
                str = str.concat(' ');
                str = str.concat(employment.streetNr);
            }
        }

        if (employment.city?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.city);
        }

        return str;
    }

    checkDocuments(): Observable<SlapStatus[]> {
        return this.slapService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    static getUrlForStep(step: SlapStatus): string {
        switch (step) {
            case SlapStatus.Person:
                return 'person';
            case SlapStatus.Contact:
                return 'contact';
            case SlapStatus.Business:
                return 'business';
            case SlapStatus.RangeOfProducts:
                return 'range-of-products';
            case SlapStatus.ProductPreparation:
                return 'product-preparation';
            case SlapStatus.InvoiceAddress:
                return 'invoice-address';
            case SlapStatus.Release:
                return 'release';
            case SlapStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }
}
